import React, { useState, useRef, useEffect } from 'react';
import { RiSearchLine, RiCloseLine } from '@remixicon/react';
import { InputRoot, Input, InputIcon } from '@/design-system/input';
import { cn } from '@/design-system/lib/utils';
import { InputSearchProps } from '@/types/components';

/** Map the app's public Size values onto the kit's InputRoot sizes. */
const kitSizeMap = {
  small: 'sm',
  medium: 'md',
  large: 'lg',
} as const;

const iconSizeMap = {
  small: 'size-3.5',
  medium: 'size-4',
  large: 'size-[18px]',
} as const;

export const InputSearch: React.FC<InputSearchProps> = ({
  placeholder = 'Search...',
  value: controlledValue,
  onChange,
  onSearch,
  onClear,
  // Accepted for API compatibility — the rendered icon is always the search
  // icon regardless of what is passed (long-standing behavior, kept as-is).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  icon = 'ri-search-line',
  size = 'medium',
  disabled = false,
  loading = false,
  debounceMs = 300,
  showClearButton = true,
  suggestions = [],
  onSuggestionClick,
  className = '',
}) => {
  const [internalValue, setInternalValue] = useState(controlledValue || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (!isControlled) {
      setInternalValue(newValue);
    }

    onChange?.(newValue);

    // Debounced search
    if (onSearch && debounceMs > 0) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        onSearch(newValue);
      }, debounceMs);
    } else if (onSearch) {
      onSearch(newValue);
    }

    // Show suggestions
    if (suggestions.length > 0 && newValue.length > 0) {
      setShowSuggestions(true);
      setFocusedIndex(-1);
    } else {
      setShowSuggestions(false);
    }
  };

  // Handle clear
  const handleClear = () => {
    const newValue = '';

    if (!isControlled) {
      setInternalValue(newValue);
    }

    onChange?.(newValue);
    onClear?.();
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    if (!isControlled) {
      setInternalValue(suggestion);
    }

    onChange?.(suggestion);
    onSuggestionClick?.(suggestion);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0) {
          handleSuggestionClick(suggestions[focusedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setFocusedIndex(-1);
        break;
    }
  };

  // Filter suggestions based on current value
  const filteredSuggestions = suggestions.filter((suggestion) =>
    suggestion.toLowerCase().includes(currentValue.toLowerCase())
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const iconSize = iconSizeMap[size];

  return (
    <div className={cn('relative', className)}>
      <InputRoot
        size={kitSizeMap[size]}
        className={cn(loading && 'animate-pulse')}
      >
        {/* Search icon — intentionally always the search icon */}
        <InputIcon>
          <RiSearchLine aria-hidden className={iconSize} />
        </InputIcon>

        <Input
          ref={inputRef}
          type="text"
          value={currentValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (filteredSuggestions.length > 0 && currentValue.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
        />

        {/* Clear button */}
        {showClearButton && currentValue.length > 0 && !loading && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            className="shrink-0 inline-flex items-center justify-center text-icon-soft-400 hover:text-text-sub-600 transition-colors focus:outline-none"
          >
            <RiCloseLine aria-hidden className={iconSize} />
          </button>
        )}
      </InputRoot>

      {/* Suggestions dropdown */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-bg-white-0 border border-stroke-soft-200 rounded-lg shadow-custom-shadows-medium max-h-60 overflow-auto"
        >
          {filteredSuggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              className={cn(
                'w-full px-4 py-2 text-left text-sm text-text-strong-950 hover:bg-bg-weak-50 focus:bg-bg-weak-50 focus:outline-none transition-colors',
                index === focusedIndex && 'bg-bg-weak-50'
              )}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default InputSearch;
